import React, { useMemo, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { motion } from "framer-motion";
import { Plus, Search, Pencil, Trash2, Power, Wallet, MessageCircle, BadgeDollarSign } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/services/api";
import { useLang } from "@/context/LanguageContext";
import { fmtMoney, fmtDate } from "@/utils/format";
import { useSingleFlightMutation } from "@/hooks/useSingleFlightMutation";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from "@/components/ui/table";
import { Skeleton } from "@/components/ui/skeleton";
import { Badge } from "@/components/ui/badge";

const EMPTY = {
  name: "", phone: "", address: "", cin: "", nif: "",
  telegram_chat_id: "", profit_type: "percent", profit_value: 3, withdrawal_method: "NatCash",
  pin: "", active: true, holds_goud: false, goud_rate: "", goud_balance: "",
};

export default function AgentsPage() {
  const { t } = useLang();
  const qc = useQueryClient();
  const [search, setSearch] = useState("");
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [fundAgent, setFundAgent] = useState(null);
  const [commissionAgent, setCommissionAgent] = useState(null);
  const [broadcastOpen, setBroadcastOpen] = useState(false);
  const [broadcastText, setBroadcastText] = useState("");
  const [broadcastResult, setBroadcastResult] = useState(null);

  const { data: agents = [], isLoading } = useQuery({
    queryKey: ["agents"],
    queryFn: async () => (await api.get("/agents")).data,
  });

  const activeAgents = useMemo(
    () => agents.filter((a) => a.active !== false),
    [agents],
  );

  const filtered = useMemo(
    () => agents.filter((a) =>
      !search ||
      a.name.toLowerCase().includes(search.toLowerCase()) ||
      a.phone.includes(search) ||
      (a.cin || "").includes(search) ||
      (a.nif || "").includes(search)
    ),
    [agents, search]
  );

  const openCreate = () => { setEditing(null); setForm(EMPTY); setModalOpen(true); };
  const openEdit = (a) => { setEditing(a); setForm({ ...EMPTY, ...a }); setModalOpen(true); };

  const saveMut = useSingleFlightMutation({
    mutationFn: async () => {
      const payload = {
        ...form,
        holds_goud: !!form.holds_goud,
        goud_rate: parseFloat(form.goud_rate) || 0,
        goud_balance: parseFloat(form.goud_balance) || 0,
      };
      if (!payload.pin) delete payload.pin;
      if (editing) return (await api.put(`/agents/${editing.id}`, payload)).data;
      return (await api.post("/agents", payload)).data;
    },
    onSuccess: (data) => {
      toast.success(editing ? "Ajan modifye" : `Ajan kreye — PIN: ${data.pin}`);
      qc.invalidateQueries({ queryKey: ["agents"] });
      setModalOpen(false);
    },
    onError: (err) => toast.error(formatApiError(err.response?.data?.detail)),
  });

  const deleteMut = useMutation({
    mutationFn: async (id) => (await api.delete(`/agents/${id}`)).data,
    onSuccess: () => { toast.success("Ajan efase"); qc.invalidateQueries({ queryKey: ["agents"] }); },
  });

  const toggleActive = useMutation({
    mutationFn: async (a) => (await api.put(`/agents/${a.id}`, { active: !a.active })).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ["agents"] }),
  });

  const broadcastMut = useMutation({
    mutationFn: async () =>
      (await api.post("/whatsapp/broadcast-agents", {
        text: broadcastText.trim(),
        active_only: true,
      })).data,
    onSuccess: (data) => {
      setBroadcastResult(data);
      if (data.failed === 0) {
        toast.success(`Mesaj ale bay ${data.sent} ajan`);
      } else {
        toast.error(`Ale: ${data.sent} — echwe: ${data.failed}`);
      }
    },
    onError: (err) => toast.error(formatApiError(err.response?.data?.detail)),
  });

  return (
    <div className="space-y-6">
      <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-3xl font-heading font-bold">{t("agents")}</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Jere rezo ajan yo — wè to komisyon, total touche, retrè, ak sa ki rete.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button
            type="button"
            variant="outline"
            className="gap-2"
            onClick={() => {
              setBroadcastText("");
              setBroadcastResult(null);
              setBroadcastOpen(true);
            }}
            data-testid="broadcast-agents-btn"
          >
            <MessageCircle className="h-4 w-4" /> Mesaj WhatsApp → tout ajan
          </Button>
          <Button onClick={openCreate} data-testid="add-agent-btn" className="bg-brand-blue hover:bg-brand-blueHover text-white gap-2">
            <Plus className="h-4 w-4" /> {t("add_agent")}
          </Button>
        </div>
      </motion.div>

      <div className="rounded-2xl border border-border bg-card">
        <div className="p-4 border-b border-border flex items-center gap-3">
          <div className="relative flex-1 max-w-sm">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={t("search")}
              className="pl-9 h-9"
              data-testid="agent-search-input"
            />
          </div>
          <div className="text-xs text-muted-foreground">{filtered.length} ajan</div>
        </div>
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{t("name")}</TableHead>
                <TableHead>{t("phone")}</TableHead>
                <TableHead>PIN</TableHead>
                <TableHead>To komisyon</TableHead>
                <TableHead>{t("profit_total")}</TableHead>
                <TableHead>{t("profit_withdrawn")}</TableHead>
                <TableHead>{t("profit_remaining")}</TableHead>
                <TableHead>Estati</TableHead>
                <TableHead className="text-right">Aksyon</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading && [...Array(3)].map((_, i) => (
                <TableRow key={i}><TableCell colSpan={9}><Skeleton className="h-8" /></TableCell></TableRow>
              ))}
              {!isLoading && filtered.length === 0 && (
                <TableRow><TableCell colSpan={9} className="text-center text-muted-foreground py-10">Pa gen ajan</TableCell></TableRow>
              )}
              {filtered.map((a) => (
                <TableRow key={a.id} data-testid={`agent-row-${a.id}`}>
                  <TableCell className="font-medium">
                    {a.name}
                    {a.holds_goud && (
                      <Badge variant="outline" className="ml-2 border-brand-gold/40 text-amber-700">
                        Goud · {a.goud_rate || "—"}
                      </Badge>
                    )}
                  </TableCell>
                  <TableCell className="font-mono text-sm">{a.phone}</TableCell>
                  <TableCell className="font-mono text-sm tracking-widest" data-testid={`agent-pin-${a.id}`}>{a.pin || "—"}</TableCell>
                  <TableCell className="text-sm">
                    {a.profit_type === "percent" ? `${a.profit_value}%` : `$${a.profit_value}`}
                  </TableCell>
                  <TableCell className="font-semibold tabular-nums" data-testid={`agent-comm-total-${a.id}`}>
                    {fmtMoney(a.commission_total ?? 0)}
                  </TableCell>
                  <TableCell className="tabular-nums text-muted-foreground" data-testid={`agent-comm-withdrawn-${a.id}`}>
                    {fmtMoney(a.commission_withdrawn ?? 0)}
                  </TableCell>
                  <TableCell
                    className={`font-semibold tabular-nums ${
                      (a.commission_remaining ?? 0) < 0 ? "text-rose-600" : "text-brand-gold"
                    }`}
                    data-testid={`agent-comm-remaining-${a.id}`}
                  >
                    {fmtMoney(a.commission_remaining ?? 0)}
                  </TableCell>
                  <TableCell>
                    <Badge variant={a.active ? "default" : "outline"} className={a.active ? "bg-emerald-500/20 text-emerald-500 border-emerald-500/30" : ""}>
                      {a.active ? t("active") : t("inactive")}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-right space-x-1">
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => setCommissionAgent(a)}
                      data-testid={`commission-agent-${a.id}`}
                      title="Detay komisyon"
                    >
                      <BadgeDollarSign className="h-4 w-4 text-brand-gold" />
                    </Button>
                    <Button variant="ghost" size="icon" onClick={() => setFundAgent(a)} data-testid={`fund-agent-${a.id}`} title={t("fund_agent")}>
                      <Wallet className="h-4 w-4 text-brand-gold" />
                    </Button>
                    <Button variant="ghost" size="icon" onClick={() => toggleActive.mutate(a)} data-testid={`toggle-agent-${a.id}`}>
                      <Power className="h-4 w-4" />
                    </Button>
                    <Button variant="ghost" size="icon" onClick={() => openEdit(a)} data-testid={`edit-agent-${a.id}`}>
                      <Pencil className="h-4 w-4" />
                    </Button>
                    <Button variant="ghost" size="icon" onClick={() => window.confirm("Konfime efasman?") && deleteMut.mutate(a.id)} data-testid={`delete-agent-${a.id}`}>
                      <Trash2 className="h-4 w-4 text-rose-500" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </div>

      <Dialog open={modalOpen} onOpenChange={setModalOpen}>
        <DialogContent
          className="bg-card max-w-2xl w-[calc(100%-0.75rem)] sm:w-full max-h-[92dvh] overflow-y-auto overscroll-contain p-4 sm:p-6 gap-3"
          data-testid="agent-form-dialog"
        >
          <DialogHeader className="sticky top-0 z-10 bg-card pb-2 pr-8">
            <DialogTitle>{editing ? t("edit") + " " + t("agent") : t("add_agent")}</DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
            <div><Label>{t("name")}</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="agent-form-name" /></div>
            <div><Label>{t("phone")}</Label><Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="agent-form-phone" /></div>
            <div>
              <Label>PIN 4 chif</Label>
              <Input
                inputMode="numeric"
                maxLength={4}
                value={form.pin || ""}
                onChange={(e) => setForm({ ...form, pin: e.target.value.replace(/\D/g, "").slice(0, 4) })}
                placeholder={editing ? "Kenbe oswa chanje" : "Opsyonèl — auto si vid"}
                data-testid="agent-form-pin"
                className="font-mono tracking-widest"
              />
              <p className="text-xs text-muted-foreground mt-1">Si ou kite vid lè w kreye, sistèm nan jenere yon PIN otomatikman.</p>
            </div>
            <div className="sm:col-span-2"><Label>{t("address")}</Label><Input value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} data-testid="agent-form-address" /></div>
            <div><Label>{t("cin")}</Label><Input value={form.cin} onChange={(e) => setForm({ ...form, cin: e.target.value })} data-testid="agent-form-cin" /></div>
            <div><Label>{t("nif")}</Label><Input value={form.nif} onChange={(e) => setForm({ ...form, nif: e.target.value })} data-testid="agent-form-nif" /></div>
            <div className="sm:col-span-2">
              <Label>Telegram Chat ID (otomatik)</Label>
              <Input
                value={form.telegram_chat_id || ""}
                readOnly
                placeholder="Lye otomatikman lè ajan an konekte (telefòn + PIN) sou Bot Ajan"
                className="bg-muted/40"
                data-testid="agent-telegram-chat-id"
              />
              <p className="text-xs text-muted-foreground mt-1">
                Pa bezwen ranpli. Ajan an voye nimewo + PIN sou Bot Ajan — sistèm nan sove Chat ID a pou notifikasyon.
              </p>
            </div>
            <div>
              <Label>Tip Pwofi</Label>
              <Select value={form.profit_type} onValueChange={(v) => setForm({ ...form, profit_type: v })}>
                <SelectTrigger className="bg-background" data-testid="agent-form-profit-type"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-popover"><SelectItem value="percent">{t("percent")}</SelectItem><SelectItem value="fixed">{t("fixed")}</SelectItem></SelectContent>
              </Select>
            </div>
            <div><Label>Valè Pwofi</Label><Input type="number" step="0.01" value={form.profit_value} onChange={(e) => setForm({ ...form, profit_value: parseFloat(e.target.value) || 0 })} data-testid="agent-form-profit-value" /></div>
            <div>
              <Label>{t("withdrawal_method")}</Label>
              <Select value={form.withdrawal_method} onValueChange={(v) => setForm({ ...form, withdrawal_method: v })}>
                <SelectTrigger className="bg-background" data-testid="agent-form-withdrawal"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-popover"><SelectItem value="NatCash">NatCash</SelectItem><SelectItem value="Cash">Cash</SelectItem></SelectContent>
              </Select>
            </div>
            <div className="flex items-center gap-3 sm:mt-6">
              <Switch checked={form.active} onCheckedChange={(v) => setForm({ ...form, active: v })} data-testid="agent-form-active" />
              <Label>{t("active")}</Label>
            </div>
            <div className="sm:col-span-2 rounded-xl border border-border p-3 space-y-3">
              <div className="flex items-center gap-3">
                <Switch
                  checked={!!form.holds_goud}
                  onCheckedChange={(v) => setForm({ ...form, holds_goud: !!v })}
                  data-testid="agent-form-holds-goud"
                />
                <Label>Ajan Goud — kenbe HTG ak to jodi a</Label>
              </div>
              {form.holds_goud && (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <Label>To jodi a (HTG pou 1 USD)</Label>
                    <Input
                      type="number"
                      step="0.01"
                      min="0"
                      value={form.goud_rate}
                      onChange={(e) => setForm({ ...form, goud_rate: e.target.value })}
                      placeholder="egz. 132"
                      data-testid="agent-form-goud-rate"
                    />
                  </div>
                  <div>
                    <Label>Balans goud nan men (HTG)</Label>
                    <Input
                      type="number"
                      step="0.01"
                      min="0"
                      value={form.goud_balance}
                      onChange={(e) => setForm({ ...form, goud_balance: e.target.value })}
                      placeholder="egz. 50000"
                      data-testid="agent-form-goud-balance"
                    />
                  </div>
                </div>
              )}
            </div>
          </div>
          <DialogFooter className="sticky bottom-0 z-10 bg-card pt-3 pb-1 border-t border-border gap-2 sm:gap-0">
            <Button variant="outline" onClick={() => setModalOpen(false)}>{t("cancel")}</Button>
            <Button className="bg-brand-blue hover:bg-brand-blueHover text-white" onClick={() => saveMut.mutate()} disabled={saveMut.isPending} data-testid="agent-form-save">
              {saveMut.isPending ? t("loading") : t("save")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <FundDialog agent={fundAgent} onClose={() => setFundAgent(null)} />
      <CommissionDialog agent={commissionAgent} onClose={() => setCommissionAgent(null)} />

      <Dialog
        open={broadcastOpen}
        onOpenChange={(o) => {
          if (!o && !broadcastMut.isPending) setBroadcastOpen(false);
        }}
      >
        <DialogContent className="bg-card max-w-lg">
          <DialogHeader>
            <DialogTitle>Mesaj WhatsApp → tout ajan</DialogTitle>
          </DialogHeader>
          <p className="text-xs text-muted-foreground">
            Mesaj la ale sou nimewo WhatsApp chak ajan aktif ({activeAgents.length} ajan).
            Se menm nimewo yo itilize pou kont ajan / bot la.
          </p>
          <div className="space-y-2">
            <Label>Mesaj</Label>
            <Textarea
              value={broadcastText}
              onChange={(e) => setBroadcastText(e.target.value)}
              rows={6}
              maxLength={3500}
              placeholder="Ekri mesaj ou a pou tout ajan yo…"
              data-testid="broadcast-agents-text"
            />
            <p className="text-[11px] text-muted-foreground text-right">
              {broadcastText.trim().length}/3500
            </p>
          </div>
          {activeAgents.length > 0 && (
            <div className="rounded-xl border border-border bg-secondary/30 p-3 max-h-28 overflow-y-auto text-xs space-y-1">
              {activeAgents.map((a) => (
                <div key={a.id} className="flex justify-between gap-2">
                  <span className="truncate">{a.name}</span>
                  <span className="font-mono shrink-0">{a.phone}</span>
                </div>
              ))}
            </div>
          )}
          {broadcastResult && (
            <div className="rounded-xl border border-border p-3 text-xs space-y-1" data-testid="broadcast-agents-result">
              <p>
                Rezilta: <strong>{broadcastResult.sent}</strong> ale ·{" "}
                <strong>{broadcastResult.failed}</strong> echwe · total{" "}
                {broadcastResult.total}
              </p>
              {(broadcastResult.results || [])
                .filter((r) => !r.ok)
                .slice(0, 8)
                .map((r) => (
                  <p key={r.agent_id} className="text-rose-600">
                    ❌ {r.name || r.to}: {r.detail}
                  </p>
                ))}
            </div>
          )}
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setBroadcastOpen(false)}
              disabled={broadcastMut.isPending}
            >
              Fèmen
            </Button>
            <Button
              className="bg-emerald-600 hover:bg-emerald-700 text-white gap-2"
              onClick={() => broadcastMut.mutate()}
              disabled={
                broadcastMut.isPending
                || !broadcastText.trim()
                || activeAgents.length === 0
              }
              data-testid="broadcast-agents-send"
            >
              <MessageCircle className="h-4 w-4" />
              {broadcastMut.isPending
                ? "Ap voye…"
                : `Voye bay ${activeAgents.length} ajan`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function FundDialog({ agent, onClose }) {
  const { t } = useLang();
  const qc = useQueryClient();
  const [form, setForm] = useState({ amount: "", note: "", mesaje_id: "" });
  const open = !!agent;
  const phone = agent?.phone;

  const { data: mesajes = [] } = useQuery({
    queryKey: ["mesajes"],
    queryFn: async () => (await api.get("/mesajes")).data,
    enabled: open,
  });
  const activeMesajes = useMemo(
    () => mesajes.filter((m) => m.active !== false),
    [mesajes],
  );

  const fundingsQuery = useQuery({
    queryKey: ["agent-fundings", agent?.id],
    queryFn: async () => (await api.get(`/agents/${agent.id}/fundings`)).data,
    enabled: open && !!agent?.id,
  });
  const fundings = fundingsQuery.data || [];
  const { data: summary } = useQuery({
    queryKey: ["agent-summary", phone],
    queryFn: async () => (await api.get(`/agent/${encodeURIComponent(phone)}/summary`)).data,
    enabled: open && !!phone && fundingsQuery.isFetched && !fundingsQuery.isFetching,
  });

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ["agent-fundings", agent?.id] });
    qc.invalidateQueries({ queryKey: ["agent-summary", phone] });
  };

  const addMut = useSingleFlightMutation({
    mutationFn: async () => (await api.post(`/agents/${agent.id}/fundings`, {
      amount: parseFloat(form.amount) || 0,
      note: form.note,
      mesaje_id: form.mesaje_id || undefined,
    })).data,
    onSuccess: (data) => {
      const added = data?.amount ?? 0;
      const inHand = data?.cash_in_hand ?? 0;
      toast.success(
        data?.control_recorded
          ? data?.mesaje_name
            ? `+${fmtMoney(added)} ajoute (mesajé ${data.mesaje_name}) — ${fmtMoney(inHand)} rete nan men`
            : `+${fmtMoney(added)} ajoute — ${fmtMoney(inHand)} rete nan men + Tablo Kontwòl`
          : t("add_funding"),
      );
      setForm({ amount: "", note: "", mesaje_id: "" });
      invalidate();
      qc.invalidateQueries({ queryKey: ["control-week"] });
      qc.invalidateQueries({ queryKey: ["control-weeks"] });
    },
    onError: (err) => toast.error(formatApiError(err.response?.data?.detail)),
  });

  const repairMut = useMutation({
    mutationFn: async () =>
      (await api.post(`/agents/${agent.id}/repairs/whatsapp-missed-delivery`)).data,
    onSuccess: (data) => {
      const n = data?.marked_n || 0;
      const hand = data?.cash_in_hand;
      toast.success(
        n
          ? `Livrezon WhatsApp korije — ${fmtMoney(hand ?? 0)} rete nan men`
          : "Pa gen livrezon WhatsApp pou korije",
      );
      invalidate();
    },
    onError: (err) => toast.error(formatApiError(err.response?.data?.detail)),
  });

  const delMut = useMutation({
    mutationFn: async (fid) => (await api.delete(`/agents/${agent.id}/fundings/${fid}`)).data,
    onSuccess: (data) => {
      if (data?.admin_wallet_restored) {
        toast.success("Efasé — kob retounen nan wallet admin");
      }
      invalidate();
      qc.invalidateQueries({ queryKey: ["control-week"] });
      qc.invalidateQueries({ queryKey: ["control-weeks"] });
    },
    onError: (err) => toast.error(formatApiError(err.response?.data?.detail)),
  });

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-card max-w-2xl w-[calc(100%-0.75rem)] sm:w-full max-h-[92dvh] overflow-y-auto overscroll-contain p-4 sm:p-6">
        <DialogHeader>
          <DialogTitle>{t("fund_agent")} — {agent?.name}</DialogTitle>
        </DialogHeader>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <div className="rounded-xl bg-secondary/50 p-3">
            <div className="text-[10px] uppercase tracking-widest text-muted-foreground">{t("total_funded")}</div>
            <div className="text-lg font-heading font-bold text-brand-gold mt-1" data-testid="fund-total-funded">{fmtMoney(summary?.total_funded ?? 0)}</div>
            <div className="text-[10px] text-muted-foreground mt-0.5">Total ajoute sou kont</div>
          </div>
          <div className="rounded-xl bg-secondary/50 p-3">
            <div className="text-[10px] uppercase tracking-widest text-muted-foreground">{t("cash_delivered")}</div>
            <div className="text-lg font-heading font-bold text-sky-500 mt-1" data-testid="fund-cash-delivered">{fmtMoney(summary?.cash_delivered ?? 0)}</div>
            <div className="text-[10px] text-muted-foreground mt-0.5">Deja bay kliyan</div>
          </div>
          <div className="rounded-xl bg-secondary/50 p-3">
            <div className="text-[10px] uppercase tracking-widest text-muted-foreground">{t("cash_in_hand")}</div>
            <div className="text-lg font-heading font-bold text-emerald-500 mt-1" data-testid="fund-cash-in-hand">{fmtMoney(summary?.cash_in_hand ?? 0)}</div>
            <div className="text-[10px] text-muted-foreground mt-0.5">Resevwa − livre (− remèt)</div>
          </div>
        </div>

        {Number(summary?.cash_in_hand) === 1116 && (
          <div className="rounded-xl border border-amber-500/40 bg-amber-500/10 p-3 space-y-2">
            <p className="text-xs text-muted-foreground">
              WhatsApp te konfime yon livrezon $900 san li pa retire balans lan.
              Klike pou make l livre — balans lan dwe tounen $216.
            </p>
            <Button
              type="button"
              className="w-full bg-amber-600 hover:bg-amber-700 text-white"
              disabled={repairMut.isPending}
              onClick={() => repairMut.mutate()}
              data-testid="fund-repair-whatsapp-delivery"
            >
              {repairMut.isPending ? "Ap korije…" : "Korije livrezon $900"}
            </Button>
          </div>
        )}

        <div className="grid sm:grid-cols-2 gap-3 items-end">
          <div>
            <Label>{t("funding_amount")} (USD)</Label>
            <Input type="number" step="0.01" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} data-testid="fund-amount-input" />
          </div>
          <div>
            <Label>Mesajé (moun ki pote lajan)</Label>
            <Select
              value={form.mesaje_id || "__none__"}
              onValueChange={(v) => setForm({ ...form, mesaje_id: v === "__none__" ? "" : v })}
            >
              <SelectTrigger data-testid="fund-mesaje-select">
                <SelectValue placeholder="Chwazi mesajé (opsyonèl)" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__none__">San mesajé (admin dirèk)</SelectItem>
                {activeMesajes.map((m) => (
                  <SelectItem key={m.id} value={m.id}>{m.name} — {m.phone}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-[11px] text-muted-foreground mt-1">
              Si mesajé pote lajan nan yon <strong>misyon</strong>, pa ajoute isit la —
              tann konfimasyon misyon an (sinon Caisse Admin ka vin negatif).
              Itilize jaden sa a sèlman pou lajan admin bay ajan dirèkteman.
            </p>
          </div>
          <div className="sm:col-span-2">
            <Label>{t("funding_note")}</Label>
            <Input value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} data-testid="fund-note-input" />
          </div>
          <div className="sm:col-span-2 flex justify-end">
            <Button
              className="bg-brand-gold hover:bg-brand-goldHover text-slate-900 font-semibold"
              onClick={() => addMut.mutate()}
              disabled={addMut.isPending || !(parseFloat(form.amount) > 0)}
              data-testid="fund-add-btn"
            >
              {addMut.isPending ? t("loading") : t("add_funding")}
            </Button>
          </div>
        </div>

        <div className="rounded-xl border border-border overflow-hidden max-h-72 overflow-y-auto">
          <div className="px-3 py-2 text-xs uppercase tracking-widest text-muted-foreground border-b border-border">{t("funding_history")}</div>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{t("amount")}</TableHead>
                <TableHead>{t("funding_note")}</TableHead>
                <TableHead>Mesajé</TableHead>
                <TableHead>Dat</TableHead>
                <TableHead className="text-right">Aksyon</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {fundings.length === 0 && (
                <TableRow><TableCell colSpan={5} className="text-center py-6 text-muted-foreground">{t("no_funding")}</TableCell></TableRow>
              )}
              {fundings.map((f) => (
                <TableRow key={f.id} data-testid={`funding-row-${f.id}`}>
                  <TableCell className={`font-semibold ${f.amount < 0 ? "text-rose-600" : ""}`}>
                    {f.amount < 0 ? "−" : "+"}{fmtMoney(Math.abs(f.amount || 0))}
                  </TableCell>
                  <TableCell className="text-sm text-muted-foreground">{f.note || "—"}</TableCell>
                  <TableCell className="text-sm">{f.mesaje_name || "—"}</TableCell>
                  <TableCell className="text-xs text-muted-foreground">{fmtDate(f.created_at)}</TableCell>
                  <TableCell className="text-right">
                    <Button variant="ghost" size="icon" onClick={() => window.confirm("Konfime efasman?") && delMut.mutate(f.id)} data-testid={`delete-funding-${f.id}`}>
                      <Trash2 className="h-4 w-4 text-rose-500" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>{t("cancel")}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function CommissionDialog({ agent, onClose }) {
  const { t } = useLang();
  const qc = useQueryClient();
  const open = !!agent;
  const [penaltyAmount, setPenaltyAmount] = useState("");
  const [penaltyReason, setPenaltyReason] = useState("");

  const { data, isLoading } = useQuery({
    queryKey: ["agent-commission", agent?.id],
    queryFn: async () => (await api.get(`/agents/${agent.id}/commission`)).data,
    enabled: open,
  });

  const penaltyMut = useSingleFlightMutation({
    mutationFn: async () =>
      (await api.post(`/agents/${agent.id}/penalties`, {
        amount: parseFloat(penaltyAmount) || 0,
        reason: penaltyReason.trim(),
      })).data,
    onSuccess: (res) => {
      toast.success(
        `Penalite anrejistre · pwofi ki rete: ${fmtMoney(res?.commission_remaining ?? 0)}`,
      );
      setPenaltyAmount("");
      setPenaltyReason("");
      qc.invalidateQueries({ queryKey: ["agent-commission", agent.id] });
      qc.invalidateQueries({ queryKey: ["agents"] });
    },
    onError: (err) => toast.error(formatApiError(err.response?.data?.detail)),
  });

  const delPenaltyMut = useMutation({
    mutationFn: async (pid) =>
      (await api.delete(`/agents/${agent.id}/penalties/${pid}`)).data,
    onSuccess: (res) => {
      toast.success(
        `Penalite efase · pwofi ki rete: ${fmtMoney(res?.commission_remaining ?? 0)}`,
      );
      qc.invalidateQueries({ queryKey: ["agent-commission", agent.id] });
      qc.invalidateQueries({ queryKey: ["agents"] });
    },
    onError: (err) => toast.error(formatApiError(err.response?.data?.detail)),
  });

  const rate = agent
    ? (agent.profit_type === "percent" ? `${agent.profit_value}%` : `$${agent.profit_value}`)
    : "—";
  const remaining = data?.commission_remaining ?? agent?.commission_remaining ?? 0;

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent
        className="bg-card max-w-2xl w-[calc(100%-0.75rem)] sm:w-full max-h-[92dvh] overflow-y-auto overscroll-contain p-4 sm:p-6"
        data-testid="commission-dialog"
      >
        <DialogHeader>
          <DialogTitle>Komisyon — {agent?.name}</DialogTitle>
        </DialogHeader>
        <p className="text-xs text-muted-foreground">
          To: <span className="font-mono font-semibold text-foreground">{rate}</span>
          {" · "}
          Telefòn: <span className="font-mono">{agent?.phone}</span>
        </p>

        <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
          <div className="rounded-xl bg-secondary/50 p-3">
            <div className="text-[10px] uppercase tracking-widest text-muted-foreground">{t("profit_total")}</div>
            <div className="text-lg font-heading font-bold mt-1" data-testid="comm-detail-total">
              {fmtMoney(data?.commission_total ?? agent?.commission_total ?? 0)}
            </div>
          </div>
          <div className="rounded-xl bg-secondary/50 p-3">
            <div className="text-[10px] uppercase tracking-widest text-muted-foreground">{t("profit_withdrawn")}</div>
            <div className="text-lg font-heading font-bold text-muted-foreground mt-1" data-testid="comm-detail-withdrawn">
              {fmtMoney(data?.commission_withdrawn ?? agent?.commission_withdrawn ?? 0)}
            </div>
          </div>
          <div className="rounded-xl bg-secondary/50 p-3">
            <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Penalite</div>
            <div className="text-lg font-heading font-bold text-rose-600 mt-1" data-testid="comm-detail-penalties">
              {fmtMoney(data?.commission_penalties ?? agent?.commission_penalties ?? 0)}
            </div>
          </div>
          <div className="rounded-xl bg-secondary/50 p-3">
            <div className="text-[10px] uppercase tracking-widest text-muted-foreground">{t("profit_remaining")}</div>
            <div
              className={`text-lg font-heading font-bold mt-1 ${remaining < 0 ? "text-rose-600" : "text-brand-gold"}`}
              data-testid="comm-detail-remaining"
            >
              {fmtMoney(remaining)}
            </div>
          </div>
          <div className="rounded-xl bg-secondary/50 p-3">
            <div className="text-[10px] uppercase tracking-widest text-muted-foreground">{t("delivered")}</div>
            <div className="text-lg font-heading font-bold mt-1" data-testid="comm-detail-delivered">
              {data?.delivered_count ?? agent?.delivered_count ?? 0}
            </div>
          </div>
        </div>

        {Number(data?.commission_adjustment) < 0 && (
          <p className="text-xs text-amber-700 dark:text-amber-300" data-testid="comm-adjustment-note">
            Koreksyon {fmtMoney(data.commission_adjustment)} sou pwofi a. Li fèt yon sèl fwa.
          </p>
        )}

        <div className="rounded-xl border border-rose-500/25 bg-rose-500/5 p-3 space-y-3" data-testid="penalty-form">
          <div className="text-xs uppercase tracking-widest text-rose-600 font-semibold">
            Penalize balans pwofi (kontwòl mal pase)
          </div>
          <p className="text-xs text-muted-foreground">
            Si montan an pi gwo pase sa ki rete, balans lan ka vin negatif jiskaske ajan an touche plis pwofi.
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-[1fr_2fr_auto] gap-2 items-end">
            <div>
              <Label>Montan ($)</Label>
              <Input
                type="number"
                step="0.01"
                min="0.01"
                value={penaltyAmount}
                onChange={(e) => setPenaltyAmount(e.target.value)}
                placeholder="0.00"
                data-testid="penalty-amount-input"
              />
            </div>
            <div>
              <Label>Rezon / nòt</Label>
              <Input
                value={penaltyReason}
                onChange={(e) => setPenaltyReason(e.target.value)}
                placeholder="Kontwòl mal pase…"
                data-testid="penalty-reason-input"
              />
            </div>
            <Button
              className="bg-rose-600 hover:bg-rose-700 text-white"
              disabled={penaltyMut.isPending || !(parseFloat(penaltyAmount) > 0)}
              data-testid="penalty-submit-btn"
              onClick={() => {
                const amt = parseFloat(penaltyAmount) || 0;
                if (!(amt > 0)) return;
                if (!window.confirm(`Penalize ${fmtMoney(amt)} sou pwofi ${agent?.name}?`)) return;
                penaltyMut.mutate();
              }}
            >
              Penalize
            </Button>
          </div>
        </div>

        {(data?.penalties || []).length > 0 && (
          <div className="rounded-xl border border-border overflow-hidden max-h-48 overflow-y-auto">
            <div className="px-3 py-2 text-xs uppercase tracking-widest text-muted-foreground border-b border-border">
              Istorik penalite
            </div>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{t("amount")}</TableHead>
                  <TableHead>Rezon</TableHead>
                  <TableHead>Dat</TableHead>
                  <TableHead className="text-right">Aksyon</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {(data?.penalties || []).map((p) => (
                  <TableRow key={p.id} data-testid={`penalty-row-${p.id}`}>
                    <TableCell className="font-semibold text-rose-600 tabular-nums">
                      −{fmtMoney(p.amount ?? 0)}
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground">{p.reason || "—"}</TableCell>
                    <TableCell className="text-xs text-muted-foreground">{fmtDate(p.created_at)}</TableCell>
                    <TableCell className="text-right">
                      <Button
                        variant="ghost"
                        size="icon"
                        data-testid={`delete-penalty-${p.id}`}
                        onClick={() => window.confirm("Efase penalite sa a?") && delPenaltyMut.mutate(p.id)}
                      >
                        <Trash2 className="h-4 w-4 text-rose-500" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}

        <div className="rounded-xl border border-border overflow-hidden max-h-72 overflow-y-auto">
          <div className="px-3 py-2 text-xs uppercase tracking-widest text-muted-foreground border-b border-border">
            Livrezon (komisyon pa transfè)
          </div>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>No#</TableHead>
                <TableHead>Benefisyè</TableHead>
                <TableHead>{t("amount")}</TableHead>
                <TableHead>Komisyon</TableHead>
                <TableHead>Dat</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading && (
                <TableRow><TableCell colSpan={5}><Skeleton className="h-8" /></TableCell></TableRow>
              )}
              {!isLoading && (data?.transfers || []).length === 0 && (
                <TableRow>
                  <TableCell colSpan={5} className="text-center py-6 text-muted-foreground">
                    Pa gen livrezon ankò
                  </TableCell>
                </TableRow>
              )}
              {(data?.transfers || []).map((tr) => (
                <TableRow key={tr.transfer_id}>
                  <TableCell className="font-mono text-xs">{tr.transfer_id}</TableCell>
                  <TableCell className="text-sm">{tr.client_name || "—"}</TableCell>
                  <TableCell className="tabular-nums">{fmtMoney(tr.amount)}</TableCell>
                  <TableCell className="font-semibold tabular-nums text-brand-gold">
                    {fmtMoney(tr.agent_profit ?? 0)}
                  </TableCell>
                  <TableCell className="text-xs text-muted-foreground">
                    {fmtDate(tr.delivered_at || tr.created_at)}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>{t("cancel")}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
